import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Check, Coins, ShoppingCart, Plus, Minus, Loader2, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { formatCredits, formatPrice, formatDate, getPlanColor } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'

// Plan order for upgrade recommendation (higher number = higher tier)
const PLAN_ORDER: Record<string, number> = {
  trial: 0,
  basic: 1,
  pro: 2,
  enterprise: 3,
}

// Plan badge styles
const PLAN_BADGE: Record<string, { label: string; className: string }> = {
  trial: { label: 'Free Trial', className: 'bg-gray-100 text-gray-700' },
  basic: { label: 'Basic', className: 'bg-blue-100 text-blue-700' },
  pro: { label: 'Pro', className: 'bg-purple-100 text-purple-700' },
  enterprise: { label: 'Enterprise', className: 'bg-amber-100 text-amber-700' },
}

// Get the next tier plan to recommend based on current plan
function getRecommendedPlan(currentPlanType: string | undefined): string | null {
  if (!currentPlanType) return 'basic'
  const currentOrder = PLAN_ORDER[currentPlanType] ?? 0
  // Find the plan one tier above current
  const nextPlan = Object.entries(PLAN_ORDER).find(([, order]) => order === currentOrder + 1)
  return nextPlan ? nextPlan[0] : null // null if already at highest tier
}

// Action types grouped by category (matches sidebar order)
const USAGE_CATEGORIES = [
  {
    category: 'Image',
    items: [
      { key: 'competitor_research', label: 'Competitor Research' },
      { key: 'variation', label: 'Variation' },
      { key: 'image_freestyle', label: 'Freestyle' },
    ],
  },
  {
    category: 'Video',
    items: [
      { key: 'digital_human', label: 'Digital Human' },
      { key: 'video_freestyle', label: 'Freestyle' },
    ],
  },
] as const

export function SubscriptionPage() {
  const { subscription, fetchSubscription } = useAuthStore()
  const [purchaseQuantity, setPurchaseQuantity] = useState<Record<string, number>>({})
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()

  // Handle Stripe redirect query params (once on mount)
  useEffect(() => {
    const success = searchParams.get('success')
    const cancelled = searchParams.get('cancelled')

    if (success === 'true') {
      toast({ title: 'Payment successful! Your subscription has been upgraded.' })
      setSearchParams({}, { replace: true })
    } else if (cancelled === 'true') {
      toast({ title: 'Checkout cancelled', variant: 'destructive' })
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch subscription on mount
  useEffect(() => {
    setIsLoadingSubscription(true)
    fetchSubscription().finally(() => setIsLoadingSubscription(false))
  }, [fetchSubscription])

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
  })

  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getCreditsUsage(30),
  })

  const { data: creditPackages } = useQuery({
    queryKey: ['creditPackages'],
    queryFn: () => api.getCreditPackages(),
  })

  const checkoutMutation = useMutation({
    mutationFn: (planType: string) =>
      api.createCheckoutSession(
        planType,
        `${window.location.origin}${import.meta.env.BASE_URL}settings/subscription?success=true`,
        `${window.location.origin}${import.meta.env.BASE_URL}settings/subscription?cancelled=true`
      ),
    onSuccess: (data) => {
      window.location.href = data.checkout_url
    },
    onError: () => {
      toast({ title: 'Failed to start checkout', variant: 'destructive' })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelSubscription(),
    onSuccess: () => {
      fetchSubscription()
      toast({
        title: 'Subscription scheduled for cancellation',
        description: 'Your subscription will remain active until the end of the current billing period.',
      })
    },
    onError: () => {
      toast({ title: 'Failed to cancel subscription', variant: 'destructive' })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: () => api.reactivateSubscription(),
    onSuccess: () => {
      fetchSubscription()
      toast({ title: 'Subscription reactivated! You will continue to be billed.' })
    },
    onError: () => {
      toast({ title: 'Failed to reactivate subscription', variant: 'destructive' })
    },
  })

  const purchaseCreditsMutation = useMutation({
    mutationFn: ({ packageId, quantity }: { packageId: string; quantity: number }) =>
      api.createCreditPurchase(packageId, quantity),
    onSuccess: (data) => {
      // In a production app, you would use Stripe Elements to complete the payment
      // For now, we'll show the client secret for testing
      toast({
        title: 'Purchase initiated',
        description: 'Complete payment with Stripe (client_secret received)',
      })
      console.log('Stripe client_secret:', data.client_secret)
      // TODO: Integrate Stripe Elements for payment completion
    },
    onError: () => {
      toast({ title: 'Failed to initiate purchase', variant: 'destructive' })
    },
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Current Plan */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Current Plan</h2>

        {isLoadingSubscription && !subscription ? (
          <div className="space-y-4">
            <div className="h-8 bg-gray-200/80 animate-pulse rounded w-1/4" />
            <div className="h-4 bg-gray-200/80 animate-pulse rounded w-1/2" />
            <div className="flex gap-4 mt-4">
              <div className="h-16 bg-gray-200/80 animate-pulse rounded-lg flex-1" />
              <div className="h-16 bg-gray-200/80 animate-pulse rounded-lg flex-1" />
            </div>
          </div>
        ) : subscription ? (
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold capitalize ${getPlanColor(subscription.plan_type)}`}>
                  {subscription.plan_type}
                </span>
                {(() => {
                  const badge = PLAN_BADGE[subscription.plan_type]
                  return badge ? (
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${badge.className}`}>
                      {badge.label}
                    </span>
                  ) : null
                })()}
              </div>

              {subscription.status === 'past_due' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Payment Failed</p>
                    <p className="text-sm text-red-600">
                      Your last payment was unsuccessful. Please update your payment method to avoid losing access.
                      Your purchased credits are still available.
                    </p>
                  </div>
                </div>
              )}
              {subscription.is_expired && subscription.status !== 'past_due' && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Subscription Period Ended</p>
                    <p className="text-sm text-amber-600">
                      Your billing period has ended. Subscription credits are no longer available.
                      Please renew or purchase credits to continue.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Coins className="h-5 w-5 text-amber-500" />
                  <span className="text-lg font-semibold">
                    {formatCredits(subscription.credits_available)} credits available
                  </span>
                </div>
                <div className="text-sm text-gray-600 ml-7 space-y-1">
                  <div>
                    {subscription.is_trial ? 'Trial' : 'Subscription'}:{' '}
                    {formatCredits(subscription.subscription_credits ?? (subscription.credits_total - subscription.credits_used))}
                    {subscription.current_period_end && !subscription.is_trial && (
                      <span className="text-gray-400"> (resets {formatDate(subscription.current_period_end)})</span>
                    )}
                    {subscription.is_trial && <span className="text-gray-400"> (never expires)</span>}
                  </div>
                  <div>
                    Purchased: {formatCredits(subscription.purchased_credits ?? 0)}
                    <span className="text-gray-400"> (never expires)</span>
                  </div>
                </div>
              </div>

              {subscription.cancel_at_period_end && subscription.current_period_end && (
                <div className="mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-orange-800">
                        Subscription scheduled for cancellation
                      </p>
                      <p className="text-sm text-orange-700 mt-1">
                        Your subscription will end on {formatDate(subscription.current_period_end)}.
                        After that, you will lose access to your weekly credits
                        and be downgraded to the free plan.
                      </p>
                      <Button
                        className="mt-3"
                        variant="default"
                        onClick={() => reactivateMutation.mutate()}
                        disabled={reactivateMutation.isPending}
                      >
                        {reactivateMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Reactivating...
                          </>
                        ) : (
                          'Keep My Subscription'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {subscription.current_period_end && !subscription.cancel_at_period_end && !subscription.is_trial && (
                <p className="text-sm text-gray-600 mt-2">
                  Renews on {formatDate(subscription.current_period_end)}
                </p>
              )}
            </div>

            {!subscription.is_trial && !subscription.cancel_at_period_end && (
              <Button
                variant="outline"
                onClick={() => {
                  if (window.confirm(
                    'Are you sure you want to cancel? Your subscription will remain active until ' +
                    formatDate(subscription.current_period_end ?? '') +
                    ', but you will not be billed again after that.'
                  )) {
                    cancelMutation.mutate()
                  }
                }}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No subscription data available.</p>
        )}
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans?.filter((plan) => plan.plan_type !== 'trial').map((plan) => {
            const isCurrent = subscription?.plan_type === plan.plan_type
            const currentOrder = PLAN_ORDER[subscription?.plan_type ?? 'trial'] ?? 0
            const planOrder = PLAN_ORDER[plan.plan_type] ?? 0
            const isUpgrade = planOrder > currentOrder
            const isRecommended = !isCurrent && getRecommendedPlan(subscription?.plan_type) === plan.plan_type
            return (
            <div
              key={plan.plan_type}
              className={`bg-white rounded-xl border border-gray-200/60 shadow-sm flex flex-col overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${
                isCurrent ? 'ring-2 ring-green-500' : isRecommended ? 'ring-2 ring-primary shadow-lg shadow-primary/10' : ''
              }`}
            >
              {/* Gradient top strip */}
              <div className={`h-1 ${
                plan.plan_type === 'basic' ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                : plan.plan_type === 'pro' ? 'bg-gradient-to-r from-purple-400 to-purple-600'
                : 'bg-gradient-to-r from-amber-400 to-amber-600'
              }`} />
              <div className="p-6 flex flex-col flex-grow">
              {/* Fixed height for badge area to ensure alignment */}
              <div className="h-4 mb-2">
                {isCurrent && (
                  <span className="text-[10px] font-medium text-green-600">CURRENT PLAN</span>
                )}
                {isRecommended && !isCurrent && (
                  <span className="text-[10px] font-medium text-primary">RECOMMENDED</span>
                )}
              </div>
              <h3 className="text-lg font-semibold">{plan.name}</h3>
              <div className="mt-2">
                <span className="text-3xl font-bold">{formatPrice(plan.price_cents)}</span>
                {plan.billing_cycle !== 'none' && (
                  <span className="text-gray-500">/{plan.billing_cycle}</span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2">{plan.credits} credits</p>

              <ul className="mt-4 space-y-2 flex-grow">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                className="w-full mt-6"
                variant={isCurrent ? 'outline' : 'default'}
                disabled={isCurrent || checkoutMutation.isPending}
                onClick={() => checkoutMutation.mutate(plan.plan_type)}
              >
                {isCurrent
                  ? 'Current Plan'
                  : isUpgrade
                    ? 'Upgrade'
                    : 'Downgrade'}
              </Button>
              </div>
            </div>
          )})}
        </div>
      </div>

      {/* Buy Credits */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Buy Extra Credits</h2>
        <p className="text-sm text-gray-600 mb-4">
          Purchase additional credits that never expire. Credits are consumed after your subscription credits.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {creditPackages?.map((pkg) => {
            const quantity = purchaseQuantity[pkg.id] || 1
            const maxQuantity = pkg.max_quantity || 10  // Fallback for API compatibility
            const totalPrice = pkg.price_cents * quantity
            const totalCredits = pkg.credits * quantity
            return (
              <div
                key={pkg.id}
                className="border rounded-lg p-4 hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{pkg.display_name}</span>
                </div>
                <div className="text-lg text-gray-600 mb-2">
                  {formatPrice(pkg.price_cents)} each
                </div>

                {/* Quantity selector */}
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPurchaseQuantity(prev => ({
                      ...prev,
                      [pkg.id]: Math.max(1, (prev[pkg.id] || 1) - 1)
                    }))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPurchaseQuantity(prev => ({
                      ...prev,
                      [pkg.id]: Math.min(maxQuantity, (prev[pkg.id] || 1) + 1)
                    }))}
                    disabled={quantity >= maxQuantity}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Total display */}
                <div className="text-center mb-3">
                  <div className="text-2xl font-bold text-primary">
                    {formatPrice(totalPrice)}
                  </div>
                  <div className="text-sm text-gray-600">
                    for {totalCredits} credits
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-3 text-center">Never expires</p>
                <Button
                  className="w-full"
                  onClick={() => purchaseCreditsMutation.mutate({ packageId: pkg.id, quantity })}
                  disabled={purchaseCreditsMutation.isPending}
                >
                  Buy Now
                </Button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Usage Summary */}
      {usage && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Usage (Last 30 Days)</h2>

          {/* Total Credits */}
          <div className="p-4 bg-gray-50 rounded-lg mb-6">
            <div className="text-2xl font-bold">
              {usage.summary.total_credits_consumed.toFixed(0)}
            </div>
            <div className="text-sm text-gray-600">Total Credits Used</div>
          </div>

          {/* By Category */}
          <div className="space-y-6">
            {USAGE_CATEGORIES.map(({ category, items }) => (
              <div key={category}>
                <h3 className="text-sm font-medium text-gray-500 mb-3">{category}</h3>
                <div className="grid grid-cols-3 gap-4">
                  {items.map(({ key, label }) => (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold">
                        {((usage.summary.by_action[key] as number) || 0).toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
